/**
 * The app frame (docs/52 §2.1): forest top bar with the business and location, bell and avatar; five tabs at the
 * bottom on phones and a left rail from 768 px; the offline bar under the top bar; toasts; the update prompt.
 */
import { Button, Icon, Sheet, ToastRegion, type IconName } from '@ff/app-ui';
import type { ComponentChildren, JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { useOnline, useQuery } from '../lib/data.ts';
import { outbox } from '../lib/outbox.ts';
import { location, navigate, scoped } from '../lib/router.ts';
import { can, switchTenant, tabsFor, useReady } from '../lib/session.ts';
import { useStore } from '../lib/store.ts';
import { dismissToast, toast, toasts } from '../lib/ui.ts';
import { applyUpdate, setBadge, updateReady } from '../pwa/pwa.ts';
import { S } from '@ff/app-ui/copy';

type Tab = 'home' | 'leads' | 'reviews' | 'jobs' | 'inbox' | 'account';

const TAB_META: Record<Tab, { href: string; icon: IconName; label: string }> = {
  home: { href: '/', icon: 'home', label: S.nav.home },
  leads: { href: '/leads', icon: 'leads', label: S.nav.leads },
  reviews: { href: '/reviews', icon: 'reviews', label: S.nav.reviews },
  jobs: { href: '/jobs', icon: 'jobs', label: S.nav.jobs },
  inbox: { href: '/inbox', icon: 'inbox', label: S.nav.inbox },
  account: { href: '/account', icon: 'user', label: S.nav.account },
};

export function activeTab(path: string): Tab {
  if (path.startsWith('/leads')) return 'leads';
  if (path.startsWith('/reviews')) return 'reviews';
  if (path.startsWith('/jobs')) return 'jobs';
  if (path.startsWith('/inbox')) return 'inbox';
  if (path.startsWith('/account') || path.startsWith('/hours') || path.startsWith('/banner') || path.startsWith('/reports') || path.startsWith('/help') || path.startsWith('/notifications')) return 'account';
  return 'home';
}

function initials(name: string | null, email: string): string {
  const src = name?.trim() || email;
  const parts = src.split(/[\s@.]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (name ? (parts[1]?.[0] ?? '') : '')).toUpperCase();
}

function useBadges() {
  const s = useReady();
  const m = s.membership;
  const leads = useQuery('leads', { query: { filter: 'new', limit: 1 } }, { enabled: can(m, 'leads.read') });
  const inbox = useQuery('inbox', {}, { enabled: can(m, 'inbox.read') });
  const notes = useQuery('notifications', { query: { limit: 30 } });
  const reviews = useQuery('reviews', { query: { filter: 'needs_you', limit: 1 } }, { enabled: can(m, 'reviews.reply') });
  const b = {
    leads: leads.data?.counts.new ?? 0,
    inbox: inbox.data?.waitingOnYou.length ?? 0,
    reviews: reviews.data?.items.filter((r) => r.needsYou).length ?? 0,
    bell: notes.data?.unread ?? 0,
  };
  useEffect(() => setBadge(b.leads + b.inbox), [b.leads, b.inbox]);
  return b;
}

export function Frame({ children }: { children: ComponentChildren }): JSX.Element {
  const s = useReady();
  const loc = useStore(location);
  const isOnline = useOnline();
  const queued = useStore(outbox);
  const toastList = useStore(toasts);
  const update = useStore(updateReady);
  const badges = useBadges();
  const tabs = tabsFor(s.membership).filter((t) => t !== 'account' || s.membership.role === 'bookkeeper' || s.membership.role === 'crew') as Tab[];
  const current = activeTab(loc.path);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    if (!isOnline) setWasOffline(true);
    else if (wasOffline) {
      toast(S.app.backOnline);
      setWasOffline(false);
    }
  }, [isOnline]);

  const badgeFor = (t: Tab): { n: number; label: string } | null => {
    if (t === 'leads' && badges.leads) return { n: badges.leads, label: S.nav.unread(badges.leads) };
    if (t === 'inbox' && badges.inbox) return { n: badges.inbox, label: S.nav.waiting(badges.inbox) };
    if (t === 'reviews' && badges.reviews) return { n: badges.reviews, label: S.nav.waiting(badges.reviews) };
    return null;
  };

  const nav = (variant: 'tabbar' | 'rail') => (
    <nav class={variant} aria-label={S.nav.label}>
      {variant === 'rail' && (
        <a class="rail-brand" href="/">
          <Logo />
          <span>{S.app.name}</span>
        </a>
      )}
      <ul role="list">
        {tabs.map((t) => {
          const meta = TAB_META[t];
          const badge = badgeFor(t);
          return (
            <li key={t}>
              <a href={scoped(meta.href, loc.scope)} class="nav-item" aria-current={current === t ? 'page' : undefined}>
                <span class="nav-icon">
                  <Icon name={meta.icon} filled={false} />
                  {badge && (
                    <span class="badge" aria-hidden="true">
                      {badge.n > 99 ? '99+' : badge.n}
                    </span>
                  )}
                </span>
                <span class="nav-label">{meta.label}</span>
                {badge && <span class="sr-only">, {badge.label}</span>}
              </a>
            </li>
          );
        })}
      </ul>
      {variant === 'rail' && (
        <div class="rail-foot">
          <a href="/account" class="nav-item" aria-current={current === 'account' ? 'page' : undefined}>
            <span class="nav-icon">
              <Icon name="user" />
            </span>
            <span class="nav-label">{S.nav.account}</span>
          </a>
        </div>
      )}
    </nav>
  );

  return (
    <div class="frame">
      <a class="skip" href="#main">
        {S.app.skip}
      </a>
      <TopBar bell={badges.bell} />
      {!isOnline && (
        <div class="offline-bar" role="status">
          <Icon name="offline" size={20} />
          <span>{queued.length ? S.app.offlineQueued(queued.length) : S.app.offline}</span>
        </div>
      )}
      {isOnline && queued.length > 0 && (
        <div class="offline-bar" data-tone="sync" role="status">
          <Icon name="upload" size={20} />
          <span>{queued.some((q) => q.failed) ? S.app.queuedFailed : S.app.queuedSending(queued.length)}</span>
        </div>
      )}
      {update && (
        <div class="update-bar" role="status">
          <span>{S.app.update}</span>
          <Button size="sm" variant="primary" onClick={applyUpdate}>
            {S.app.updateAction}
          </Button>
        </div>
      )}
      {nav('rail')}
      <main id="main" class="main" tabIndex={-1}>
        {children}
      </main>
      {tabs.length > 1 && nav('tabbar')}
      <ToastRegion toasts={toastList} onDismiss={dismissToast} />
    </div>
  );
}

export function Logo(): JSX.Element {
  return (
    <svg class="logo" viewBox="0 0 32 32" width="32" height="32" aria-hidden="true">
      <rect width="32" height="32" rx="7" fill="#0E2A1C" />
      <g fill="#3F6A51">
        <rect x="5" y="5" width="6" height="6" rx="1.5" />
        <rect x="13" y="5" width="6" height="6" rx="1.5" />
        <rect x="21" y="5" width="6" height="6" rx="1.5" />
        <rect x="5" y="13" width="6" height="6" rx="1.5" />
        <rect x="21" y="13" width="6" height="6" rx="1.5" />
        <rect x="5" y="21" width="6" height="6" rx="1.5" />
        <rect x="13" y="21" width="6" height="6" rx="1.5" />
        <rect x="21" y="21" width="6" height="6" rx="1.5" />
      </g>
      <rect x="13" y="13" width="6" height="6" rx="1.5" fill="#C8F03C" />
    </svg>
  );
}

function TopBar({ bell }: { bell: number }): JSX.Element {
  const s = useReady();
  const loc = useStore(location);
  const m = s.membership;
  const [open, setOpen] = useState(false);
  const multiLoc = m.locations.length > 1 && (m.locationIds === null || m.locationIds.length > 1);
  const multiBiz = s.me.memberships.length > 1;
  const scopeName = loc.scope ? m.locations.find((l) => l.id === loc.scope)?.name : null;
  const switcher = multiLoc || multiBiz;
  return (
    <header class="topbar">
      <div class="topbar-inner">
        {switcher ? (
          <button type="button" class="biz" aria-haspopup="dialog" onClick={() => setOpen(true)}>
            <span class="biz-name">{m.tenantName}</span>
            {multiLoc && <span class="biz-loc">{scopeName ?? S.nav.allLocations}</span>}
            <Icon name="chevron-down" size={20} />
          </button>
        ) : (
          <a class="biz" href="/">
            <span class="biz-name">{m.tenantName}</span>
          </a>
        )}
        <div class="topbar-actions">
          <a class="top-btn" href="/notifications" aria-label={bell ? `${S.nav.notifications}, ${S.nav.unread(bell)}` : S.nav.notifications}>
            <Icon name="bell" />
            {bell > 0 && (
              <span class="badge" aria-hidden="true">
                {bell > 99 ? '99+' : bell}
              </span>
            )}
          </a>
          <a class="avatar" href="/account" aria-label={S.nav.yourAccount}>
            <span aria-hidden="true">{initials(s.me.user.name, s.me.user.email)}</span>
          </a>
        </div>
      </div>
      <Sheet open={open} onClose={() => setOpen(false)} title={multiLoc ? S.nav.location : S.nav.switchBusiness} closeLabel={S.app.close}>
        <div class="stack" data-gap="s">
          {multiLoc && (
            <ul class="list" role="list">
              {[{ id: null as string | null, name: S.nav.allLocations }, ...m.locations.filter((l) => m.locationIds === null || m.locationIds.includes(l.id))].map((l) => (
                <li key={l.id ?? 'all'}>
                  <button
                    type="button"
                    class="row"
                    aria-current={loc.scope === l.id ? 'true' : undefined}
                    onClick={() => {
                      setOpen(false);
                      const path = loc.path;
                      navigate(scoped(path, l.id));
                    }}
                  >
                    <span class="row-lead">{loc.scope === l.id ? <Icon name="check" size={20} /> : null}</span>
                    <span class="row-main">
                      <span class="row-title">{l.name}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {multiBiz && (
            <ul class="list" role="list" aria-label={S.nav.switchBusiness}>
              {s.me.memberships.map((b) => (
                <li key={b.tenantId}>
                  <button
                    type="button"
                    class="row"
                    onClick={() => {
                      setOpen(false);
                      switchTenant(b.tenantId);
                    }}
                  >
                    <span class="row-lead">{b.tenantId === m.tenantId ? <Icon name="check" size={20} /> : null}</span>
                    <span class="row-main">
                      <span class="row-title">{b.tenantName}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Sheet>
    </header>
  );
}
